import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import * as amplitude from '@amplitude/analytics-browser'
import ErrorBoundary from '@components/error/boundary'
import { PGA_COACH_JOURNEY_ANALYTICS } from '@lib/pga-coach/constants'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import useOnScreen from '@hooks/useOnScreen'
import GroupProgramOffering from './group-program-offering'
import PrivateOffering from './private-offering'

const formatPrice = (offering) => {
  if (offering.priceLabel) return offering.priceLabel
  if (offering.priceInCents == null) return 'Price unavailable'
  return `$${(offering.priceInCents / 100).toFixed(0)}`
}

const formatJrlDateRange = (offering) => {
  if (!offering.startDateTime || !offering.endDateTime) {
    return 'See registration page for full program schedule'
  }
  const start = new Date(offering.startDateTime)
  const end = new Date(offering.endDateTime)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'See registration page for full program schedule'
  }
  const tz = offering.timezone || 'UTC'
  const fmt = (d) => d.toLocaleDateString('en-US', {
    month: 'short', year: 'numeric', timeZone: tz,
  })
  return `${fmt(start)} - ${fmt(end)}`
}

const formatNextAvailability = (offering) => {
  if (!offering.nextAvailableAt) return 'Availability coming soon'
  const nextAvailableAt = new Date(offering.nextAvailableAt)
  if (Number.isNaN(nextAvailableAt.getTime())) return 'Availability coming soon'
  const tz = offering.timezone || 'UTC'
  const nextDate = nextAvailableAt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  })
  const nextTime = nextAvailableAt.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  })
  return `${nextDate} at ${nextTime}`
}

const formatLocation = (offering) => (
  offering.venueName
  || [offering.city, offering.state].filter(Boolean).join(', ')
  || 'Location coming soon'
)

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

const getTagLabels = (offering) => (offering.tagLabels || [])
  .filter(Boolean)
  .slice(0, 4)
  .map(toTitleFormat)

const AVAILABILITY_WINDOW_PRIORITY = ['this_week', 'next_week', 'this_month', 'next_month']
const AVAILABILITY_WINDOW_LABELS = {
  this_week: 'this week',
  next_week: 'next week',
  this_month: 'this month',
  next_month: 'next month',
}

const formatAvailabilityWindowLabel = (offering, selectedSchedule) => {
  const windows = offering.availabilityWindows || []
  if (!windows.length) return null
  if (selectedSchedule && selectedSchedule !== 'any') {
    return windows.includes(selectedSchedule)
      ? `Has availability ${AVAILABILITY_WINDOW_LABELS[selectedSchedule] || selectedSchedule.replace(/_/g, ' ')}`
      : null
  }
  const closest = AVAILABILITY_WINDOW_PRIORITY.find((w) => windows.includes(w))
  return closest ? `Has availability ${AVAILABILITY_WINDOW_LABELS[closest]}` : null
}

/** Amplitude `source` for coach offering cards: `map`, `list`, or `dialog`. */
export const normalizePgaCoachOfferingAnalyticsSource = (source) => {
  if (source === 'map') return 'map'
  if (source === 'dialog') return 'dialog'
  return 'list'
}

/** Shared with map popup, list card, and CTA analytics so payloads stay aligned. */
export const buildPgaCoachOfferingClickAnalyticsPayload = (offering, source, position, journeyPersona) => ({
  source: normalizePgaCoachOfferingAnalyticsSource(source),
  persona: journeyPersona ?? null,
  offering_id: offering.objectID,
  offering_title: offering.title,
  format: offering.format,
  coach_name: offering.coach?.name || null,
  has_plus_subscription: offering.coachPlusBoost,
  position,
})

function PgaCoachOfferingCardInner({
  offering,
  trackingSource = 'list',
  journeyPersona,
  selectedSchedule,
  isHighlighted = false,
  openDetailsFromMapNonce,
  isOfferingFavorite = false,
  onOfferingFavoriteToggle,
  onHoverStart,
  onHoverEnd,
  onSelect,
  position,
  onCoachProfileOpen,
}) {
  const [coachInfoPanelEnabled] = useFeatureFlags([flags.FLAG_PGA_COACH_INFO_PANEL])
  const impressionRef = useRef(null)
  const lastViewedKeyRef = useRef(null)
  const isCardVisible = useOnScreen(impressionRef)
  const analyticsPayload = useMemo(
    () => buildPgaCoachOfferingClickAnalyticsPayload(offering, trackingSource, position, journeyPersona),
    [offering, trackingSource, position, journeyPersona],
  )

  useEffect(() => {
    const viewKey = `${trackingSource}:${offering.objectID}`
    if (!isCardVisible || lastViewedKeyRef.current === viewKey) return
    lastViewedKeyRef.current = viewKey
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringCardImpression, analyticsPayload)
  }, [analyticsPayload, isCardVisible, offering.objectID, trackingSource])

  const handlePrimaryActionClick = (extraAttributes = {}) => {
    const fromDialog = Boolean(extraAttributes.dialog_name)
    if (fromDialog) {
      amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringDialogActionClick, {
        ...analyticsPayload,
        ...extraAttributes,
        source: normalizePgaCoachOfferingAnalyticsSource('dialog'),
      })
      return
    }
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringActionButtonClick, {
      ...analyticsPayload,
      ...extraAttributes,
    })
  }

  const handleCardSelect = (event) => {
    if (typeof event?.target?.closest === 'function') {
      const interactive = event.target.closest('a,button,[role="button"]')
      if (interactive && interactive !== event.currentTarget) return
    }
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringClick, analyticsPayload)
    onSelect?.(offering)
  }

  const handleCardKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringClick, analyticsPayload)
    onSelect?.(offering)
  }

  const formatId = offering?.format?.toLowerCase()
  const isPrivate = formatId !== 'group' && formatId !== 'program'
  const SelectedCard = isPrivate ? PrivateOffering : GroupProgramOffering
  const availabilityWindowLabel = isPrivate
    ? formatAvailabilityWindowLabel(offering, selectedSchedule)
    : null

  const favoriteToggleProps = typeof onOfferingFavoriteToggle === 'function'
    ? {
      isOfferingFavorite,
      onToggleOfferingFavorite: onOfferingFavoriteToggle,
    }
    : {
      isOfferingFavorite: false,
      onToggleOfferingFavorite: undefined,
    }

  return (
    <SelectedCard
      rootRef={impressionRef}
      offering={offering}
      isHighlighted={isHighlighted}
      onCardSelect={handleCardSelect}
      onCardKeyDown={handleCardKeyDown}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onPrimaryActionClick={handlePrimaryActionClick}
      openDetailsFromMapNonce={openDetailsFromMapNonce}
      {...favoriteToggleProps}
      formatPrice={formatPrice}
      formatLocation={formatLocation}
      formatNextAvailability={formatNextAvailability}
      formatJrlDateRange={formatJrlDateRange}
      getTagLabels={getTagLabels}
      availabilityWindowLabel={availabilityWindowLabel}
      coachInfoPanelEnabled={coachInfoPanelEnabled}
      onCoachProfileOpen={coachInfoPanelEnabled ? onCoachProfileOpen : undefined}
    />
  )
}

PgaCoachOfferingCardInner.propTypes = {
  offering: PropTypes.shape({
    objectID: PropTypes.string,
    position: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    format: PropTypes.string,
    priceInCents: PropTypes.number,
    durationInMinutes: PropTypes.number,
    priceLabel: PropTypes.string,
    venueName: PropTypes.string,
    coachFacilityFullAddress: PropTypes.string,
    coachPlusBoost: PropTypes.bool,
    city: PropTypes.string,
    state: PropTypes.string,
    maxAttendance: PropTypes.number,
    registrationCount: PropTypes.number,
    geoDistanceMeters: PropTypes.number,
    nextAvailableAt: PropTypes.instanceOf(Date),
    hasAvailability: PropTypes.bool,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaType: PropTypes.string,
    profileUrl: PropTypes.string,
    imageUrl: PropTypes.string,
    personaTags: PropTypes.arrayOf(PropTypes.string),
    tagLabels: PropTypes.arrayOf(PropTypes.string),
    availabilityWindows: PropTypes.arrayOf(PropTypes.string),
    coach: PropTypes.shape({
      name: PropTypes.string,
      credentials: PropTypes.string,
      memberType: PropTypes.string,
      certificationLevel: PropTypes.string,
      photoUrl: PropTypes.string,
      otherOfferingsCount: PropTypes.number,
    }),
  }).isRequired,
  trackingSource: PropTypes.string,
  journeyPersona: PropTypes.string,
  selectedSchedule: PropTypes.string,
  isHighlighted: PropTypes.bool,
  openDetailsFromMapNonce: PropTypes.number,
  isOfferingFavorite: PropTypes.bool,
  onOfferingFavoriteToggle: PropTypes.func,
  onHoverStart: PropTypes.func,
  onHoverEnd: PropTypes.func,
  onSelect: PropTypes.func,
  position: PropTypes.number,
  onCoachProfileOpen: PropTypes.func,
}

function PgaCoachOfferingCard(props) {
  return (
    <ErrorBoundary>
      <PgaCoachOfferingCardInner {...props} />
    </ErrorBoundary>
  )
}

PgaCoachOfferingCard.propTypes = PgaCoachOfferingCardInner.propTypes

export default PgaCoachOfferingCard
